var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }
import { jsx, jsxs } from 'react/jsx-runtime';
import React, { useRef, useCallback, useEffect } from 'react';
import PhotoAlbum from 'react-photo-album';
import flatten from 'lodash/flatten';
import { concat, defer, BehaviorSubject } from 'rxjs';
import { Card, useTheme, Text, Stack, Dialog, TextInput, Flex, Spinner } from '@sanity/ui';
import styled from 'styled-components';
import { withLatestFrom, debounceTime, distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useClient, definePlugin } from 'sanity';
const Root = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  overflow: hidden;\n  background-origin: content-box;\n  background-repeat: no-repeat;\n  background-clip: border-box;\n  background-size: cover;\n  position: relative;\n  outline: none !important;\n  border: ", ";\n  box-sizing: content-box;\n  user-drag: none;\n\n  &:hover {\n    opacity: 0.85;\n  }\n\n  &:focus,\n  &:active {\n    border: 1px solid var(--input-border-color-focus);\n    box-shadow: inset 0 0 0 3px var(--input-border-color-focus);\n  }\n"])), _ref => {
  let {
    studioTheme
  } = _ref;
  return "1px solid ".concat(studioTheme.color.card.enabled.border);
});
const CreditLineLink = styled.a(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  text-decoration: none;\n  cursor: pointer;\n"])));
const CreditLine = styled(Card)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", ";\n  user-drag: none;\n  position: absolute;\n  background-color: var(--creditline-bg);\n  bottom: 0;\n\n  [data-ui='Text'] {\n    color: var(--creditline-fg);\n  }\n"])), _ref2 => {
  let {
    theme
  } = _ref2;
  return "\n     --creditline-fg: ".concat(theme.sanity.color.card.enabled.fg, ";\n     --creditline-bg: ").concat(theme.sanity.color.card.enabled.bg, ";\n   ");
});
const UTM_SOURCE = "sanity-plugin-asset-source-unsplash";
function Photo(props) {
  const {
    onClick,
    data,
    onKeyDown,
    onFocus,
    active,
    width,
    height
  } = props;
  const rootElm = useRef(null);
  const prevActive = useRef(false);
  const handleClick = useCallback(() => {
    onClick(data);
  }, [onClick, data]);
  const handleCreditLineClicked = useCallback(event => {
    event.stopPropagation();
    const url = "".concat(data.links.html, "?utm_source=").concat(encodeURIComponent(UTM_SOURCE), "&utm_medium=referral");
    window.open(url, data.id, "noreferrer,noopener");
  }, [data]);
  const handleKeyDown = useCallback(event => {
    onKeyDown(event);
    if (event.keyCode === 13) {
      onClick(data);
    }
  }, [onKeyDown, data, onClick]);
  const handleMouseDown = useCallback(() => {
    onFocus(data);
  }, [onFocus, data]);
  useEffect(() => {
    if (!prevActive.current && active && rootElm.current) {
      rootElm.current.focus();
      onFocus(data);
    }
    prevActive.current = active;
  }, [active]);
  const src = data.urls.small;
  const userName = data.user.name;
  const theme = useTheme().sanity;
  return /* @__PURE__ */jsx(Root, {
    ref: rootElm,
    studioTheme: theme,
    title: "Select image by ".concat(userName, " from Unsplash"),
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    onMouseDown: handleMouseDown,
    style: {
      width: "".concat(width, "px"),
      height: "".concat(height, "px"),
      backgroundImage: "url(\"".concat(src, "\")")
    },
    onClick: handleClick,
    children: /* @__PURE__ */jsx(CreditLineLink, {
      onClick: handleCreditLineClicked,
      children: /* @__PURE__ */jsx(CreditLine, {
        padding: 2,
        radius: 2,
        margin: 2,
        children: /* @__PURE__ */jsxs(Text, {
          size: 1,
          title: "Open image by ".concat(userName, " on Unsplash in new window"),
          children: ["By @", data.user.username]
        })
      })
    })
  });
}
const fetchSearch = (client, query, page, perPage) => defer(() => client.observable.request({
  url: "/addons/unsplash/search/photos?query=".concat(encodeURIComponent(query), "&page=").concat(page, "&per_page=").concat(perPage),
  withCredentials: true,
  method: "GET"
}));
const fetchList = (client, type, page, perPage) => defer(() => client.observable.request({
  url: "/addons/unsplash/photos?order_by=".concat(type, "&page=").concat(page, "&per_page=").concat(perPage),
  withCredentials: true,
  method: "GET"
}));
function fetchDownloadUrl(client, photo) {
  const downloadUrl = photo.links.download_location.replace("https://api.unsplash.com", "/addons/unsplash");
  return client.request({
    url: downloadUrl,
    withCredentials: true,
    method: "GET"
  }).then(result => {
    return result.url;
  });
}
const search = (client, query, page, resultsPerPage) => {
  return concat(query.pipe(withLatestFrom(page), debounceTime(500), distinctUntilChanged(), switchMap(_ref3 => {
    let [q, p] = _ref3;
    if (q) {
      return fetchSearch(client, q, p, resultsPerPage).pipe(distinctUntilChanged(), map(result => result.results));
    }
    return fetchList(client, "popular", p, resultsPerPage);
  })));
};
const Search = styled(Stack)(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  position: sticky;\n  top: 0;\n  z-index: 1;\n"])));
styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  overflow-y: auto;\n  max-height: 80vh;\n"])));
const RESULTS_PER_PAGE = 42;
const PHOTO_SPACING = 2;
const PHOTO_PADDING = 1;
function UnsplashAssetSource(props) {
  const client = useClient({
    apiVersion: "2022-09-01"
  });
  return /* @__PURE__ */jsx(UnsplashAssetSourceInternal, _objectSpread(_objectSpread({}, props), {}, {
    client
  }));
}
class UnsplashAssetSourceInternal extends React.Component {
  constructor() {
    super(...arguments);
    this.state = {
      cursor: 0,
      query: "",
      page: 1,
      searchResults: [[]],
      isLoading: true
    };
    this.searchSubscription = null;
    this.searchSubject$ = new BehaviorSubject("");
    this.pageSubject$ = new BehaviorSubject(1);
    this.handleSelect = photo => {
      this.setState({
        isLoading: true
      });
      return fetchDownloadUrl(this.props.client, photo).then(downloadUrl => {
        const description = photo.description || void 0;
        const asset = {
          kind: "url",
          value: downloadUrl,
          assetDocumentProps: {
            _type: "sanity.imageAsset",
            source: {
              name: "unsplash",
              id: photo.id,
              url: photo.links.html
            },
            description,
            creditLine: "".concat(photo.user.name, " by Unsplash")
          }
        };
        this.props.onSelect([asset]);
      });
    };
    this.handleClose = () => {
      this.props.onClose();
    };
    this.handleSearchTermChanged = event => {
      const query = event.currentTarget.value;
      this.setState({
        query,
        page: 1,
        searchResults: [[]],
        isLoading: true,
        cursor: 0
      });
      this.pageSubject$.next(1);
      this.searchSubject$.next(query);
    };
    this.handleScollerLoadMore = () => {
      const nextPage = this.state.page + 1;
      this.setState({
        page: nextPage,
        isLoading: true
      });
      this.pageSubject$.next(nextPage);
      this.searchSubject$.next(this.state.query);
    };
    this.handleKeyDown = event => {
      const {
        cursor
      } = this.state;
      if ((event.keyCode === 38 || event.keyCode === 37) && cursor > 0) {
        this.setState(prevState => ({
          cursor: prevState.cursor - 1
        }));
      } else if ((event.keyCode === 40 || event.keyCode === 39) && cursor < this.getPhotos().length - 1) {
        this.setState(prevState => ({
          cursor: prevState.cursor + 1
        }));
      }
    };
    this.updateCursor = photo => {
      const index = this.getPhotos().findIndex(result => result.id === photo.id);
      this.setState({
        cursor: index
      });
    };
    this.renderImage = props => {
      const {
        photo,
        layout
      } = props;
      const active = this.getPhotos().findIndex(result => result.id === photo.data.id) === this.state.cursor || false;
      return /* @__PURE__ */jsx(Photo, {
        onClick: this.handleSelect.bind(photo.data),
        onKeyDown: this.handleKeyDown,
        data: photo.data,
        width: layout.width,
        height: layout.height,
        active,
        onFocus: this.updateCursor
      });
    };
  }
  componentDidMount() {
    this.searchSubscription = search(this.props.client, this.searchSubject$, this.pageSubject$, RESULTS_PER_PAGE).subscribe({
      next: results => {
        this.setState(prev => ({
          searchResults: [...prev.searchResults, results],
          isLoading: false
        }));
      }
    });
  }
  componentWillUnmount() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }
  getPhotos() {
    return flatten(this.state.searchResults);
  }
  render() {
    const {
      query,
      searchResults,
      isLoading
    } = this.state;
    return /* @__PURE__ */jsx(Dialog, {
      id: "unsplash-asset-source",
      header: "Select image from Unsplash",
      onClose: this.handleClose,
      open: true,
      width: 4,
      children: /* @__PURE__ */jsxs(Stack, {
        space: 3,
        padding: 4,
        children: [/* @__PURE__ */jsx(Card, {
          children: /* @__PURE__ */jsxs(Search, {
            space: 3,
            children: [/* @__PURE__ */jsx(Text, {
              size: 1,
              weight: "semibold",
              children: "Search Unsplash"
            }), /* @__PURE__ */jsx(TextInput, {
              label: "Search Unsplash.com",
              placeholder: "Topics or colors",
              value: query,
              onChange: this.handleSearchTermChanged
            })]
          })
        }), !isLoading && this.getPhotos().length === 0 && /* @__PURE__ */jsx(Text, {
          size: 1,
          muted: true,
          children: "No results found"
        }), /* @__PURE__ */jsx(InfiniteScroll, {
          dataLength: this.getPhotos().length,
          next: this.handleScollerLoadMore,
          hasMore: true,
          scrollThreshold: 0.99,
          height: "60vh",
          loader: /* @__PURE__ */jsx(Flex, {
            align: "center",
            justify: "center",
            padding: 3,
            children: /* @__PURE__ */jsx(Spinner, {
              muted: true
            })
          }),
          endMessage: /* @__PURE__ */jsx(Text, {
            size: 1,
            muted: true,
            children: "No more results"
          }),
          children: searchResults.filter(photos => photos.length > 0).map((photos, index) => /* @__PURE__ */jsx(PhotoAlbum, {
            layout: "rows",
            spacing: PHOTO_SPACING,
            padding: PHOTO_PADDING,
            targetRowHeight: width => {
              if (width < 300) return 150;else if (width < 600) return 200;
              return 300;
            },
            photos: photos.map(photo => ({
              src: photo.urls.small,
              width: photo.width,
              height: photo.height,
              key: photo.id,
              data: photo
            })),
            renderPhoto: this.renderImage,
            componentsProps: {
              containerProps: {
                style: {
                  marginBottom: "".concat(PHOTO_SPACING, "px")
                }
              }
            }
          }, "gallery-".concat(query || "popular", "-").concat(index)))
        })]
      })
    });
  }
}
UnsplashAssetSourceInternal.defaultProps = {
  selectedAssets: void 0
};
const Svg = styled.svg(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  transform: scale(0.8);\n  transform-origin: top;\n"])));
function UnsplashIcon() {
  return /* @__PURE__ */jsxs(Svg, {
    role: "img",
    viewBox: "0 0 24 24",
    width: "1em",
    height: "1em",
    fill: "currentColor",
    children: [/* @__PURE__ */jsx("title", {}), /* @__PURE__ */jsx("path", {
      d: "M7.5 6.75V0h9v6.75h-9zm9 3.75H24V24H0V10.5h7.5v6.75h9V10.5z"
    })]
  });
}
const unsplashAssetSource = {
  name: "unsplash",
  title: "Unsplash",
  component: UnsplashAssetSource,
  icon: UnsplashIcon
};
const unsplashImageAsset = definePlugin({
  name: "asset-source-unsplash-plugin",
  form: {
    image: {
      assetSources: prev => {
        return [...prev, unsplashAssetSource];
      }
    }
  }
});
export { unsplashAssetSource, unsplashImageAsset };
//# sourceMappingURL=index.esm.js.map
